import { CSSProperties, ImgHTMLAttributes, SourceHTMLAttributes } from "react";

export type AspectRatios = "1/1" | "2/1" | "16/9" | "3/2" | "4/3" | "none";

export interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    alt: string;
    aspectRatio?: AspectRatios;
}

export interface PictureImageProps extends ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    alt: string;
    width?: number;
    height?: number;
}

const imageCSS = (ratio?: AspectRatios) => {
    const aspectRatioCSS =
        ratio && ratio !== "none"
            ? { aspectRatio: ratio, objectFit: "cover", width: "100%" }
            : {};
    return { maxWidth: "100%", ...aspectRatioCSS };
};

/**
 * @deprecated - Please use Picture instead
 */
export const Image = ({ src, alt, aspectRatio, ...rest }: ImageProps) => {
    return (
        <img
            src={src}
            alt={alt}
            style={imageCSS(aspectRatio) as CSSProperties}
            {...rest}
        />
    );
};

export const getImageSrc = (src: string, width?: number, height?: number) => {
    if (width) {
        const srcSet = getSrcSet(src, width, ["auto=webp"]);
        return srcSet.length > 0 ? srcSet.join(",") : src;
    } else if (height) {
        return `${src}?auto=webp&height=${height}`;
    } else {
        return `${src}?auto=webp`;
    }
};

/**
 * Calculates the optimized width and height of the image based on width of the view port and aspect ratio of width and height.
 *
 * @param maxWidth - Max width of the view port
 * @param width - original width of the image
 * @param height - original height of the image
 */
export const getImageSize = (
    maxWidth: number,
    width?: number,
    height?: number
) => {
    if (width && height && width > maxWidth) {
        return {
            width: maxWidth,
            height: Math.round(maxWidth * (height / width) * 100) / 100
        };
    } else if (width && height) {
        return { width, height };
    } else if (width && width > maxWidth) {
        return { width: maxWidth, height: maxWidth };
    } else if (width) {
        return { width, height: width };
    } else {
        return { width: maxWidth, height: maxWidth };
    }
};

export type PictureProps = {
    sources?: Array<SourceHTMLAttributes<HTMLSourceElement>>;
    image: PictureImageProps;
    className?: string;
    style?: CSSProperties;
};

/**
 * A Picture component that modifies image references to enhance web performance.
 *
 * @param sources - Array of source elements to provide multiple image source at different breakpoints.
 *  If the sources prop is provided, this component doesn't perform any additional actions, as developers possess the most knowledge on optimizing their images for web performance.
 * @param image - Brandfolder image 'src', 'original image 'width', and 'height' are necessary to fully utilize optimize the image for performance.
 *  If image is from Brandfolder and sources prop isn't provided, we set default image type to 'webp', width and height on the image src is set based on the ratio of width & height and max width of the viewport.
 * @param className - Optional class name for the picture element
 */
export const Picture = ({
    sources,
    image: { src, alt, width, height, ...imageProps },
    className,
    style
}: PictureProps) => {
    // SVG images are already optimized for responsiveness
    if (src && src.endsWith(".svg")) {
        return (
            <img
                loading="lazy"
                {...imageProps}
                src={src}
                alt={alt}
                width={width}
                height={height}
                style={{ maxWidth: "100%", ...style }}
                className={className}
            />
        );
    }

    if (sources) {
        return (
            <picture className={className}>
                {sources.map((source, index) => (
                    <source key={index} {...source} />
                ))}
                <img
                    loading="lazy"
                    {...imageProps}
                    src={src}
                    alt={alt}
                    width={width}
                    height={height}
                    style={{ maxWidth: "100%", ...style }}
                />
            </picture>
        );
    }

    if (src && src.includes("wac-cdn-bfldr.atlassian.com")) {
        const { width: tabletWidth, height: tabletHeight } = getImageSize(
            768,
            width,
            height
        );
        const { width: mobileWidth, height: mobileHeight } = getImageSize(
            480,
            width,
            height
        );

        return (
            <picture className={className}>
                {/* Mobile view port */}
                <source
                    srcSet={getImageSrc(src, mobileWidth, mobileHeight)}
                    type="image/webp"
                    media="(max-width: 480px)"
                    width={mobileWidth}
                    height={mobileHeight}
                />
                {/* Tablet view port */}
                <source
                    srcSet={getImageSrc(src, tabletWidth, tabletHeight)}
                    type="image/webp"
                    media="(max-width: 768px)"
                    width={tabletWidth}
                    height={tabletHeight}
                />
                {/* Desktop view port (Default) */}
                <img
                    loading="lazy"
                    {...imageProps}
                    src={getImageSrc(src)}
                    alt={alt}
                    width={width}
                    height={height}
                    style={{ maxWidth: "100%", ...style }}
                />
            </picture>
        );
    }

    return (
        <img
            loading="lazy"
            {...imageProps}
            src={src}
            alt={alt}
            width={width}
            height={height}
            style={{ maxWidth: "100%", ...style }}
            className={className}
        />
    );
};

/**
 * This method will allow us to specify three different images in img[srcset] at 1x, 2x, and 3x and have the browser determine the best image for the customer's dimensions and resolution.
 * This is best used when the image has a general base size (such as on a card that has a max width) and not something that can be any dimensions.
 *
 * We are not doing straight 2x and 3x resolution, instead doing 1.8 and 2.6
 * This give us smaller overall file size but still will be crisp at 2x and 3x.
 * We can adjust as we go, but that will be the starting point for this method.
 * @credit Dan Wilson
 */
export const getSrcSet = (
    src: string,
    baseWidth: number,
    queryPairs: string[],
    widthKey = "width"
) => {
    return [baseWidth, baseWidth * 1.8, baseWidth * 2.6].map((w, i) => {
        const srcSetQueryPairs: string[] = [
            ...queryPairs,
            `${widthKey}=${Math.ceil(w)}`
        ];

        return `${src}?${srcSetQueryPairs.join("&")} ${i + 1}x`;
    });
};
